import { supabase } from '../config';

type TelemetryLevel = 'debug' | 'info' | 'warn' | 'error';

interface TelemetryPayload {
  level: TelemetryLevel;
  appSurface: string;
  eventType: string;
  message?: string;
  metricName?: string;
  metricValue?: number;
  metadata?: Record<string, unknown>;
}

const APP_SURFACE = 'developer-portal';
const SESSION_KEY = 'admesh-telemetry-session';
const MAX_QUEUE = 25;
const SUPABASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_SUPABASE_URL || (window as any).VITE_SUPABASE_URL || '';

function getSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;

  const sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(SESSION_KEY, sessionId);
  return sessionId;
}

function getQueue(): TelemetryPayload[] {
  try {
    return JSON.parse(localStorage.getItem('admesh-telemetry-queue') || '[]');
  } catch {
    return [];
  }
}

function setQueue(queue: TelemetryPayload[]) {
  localStorage.setItem('admesh-telemetry-queue', JSON.stringify(queue.slice(-MAX_QUEUE)));
}

async function postTelemetry(payload: TelemetryPayload) {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/ingest-client-log`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...payload,
      route: window.location.pathname,
      sessionId: getSessionId(),
      environment: process.env.NODE_ENV || 'development',
      releaseVersion: process.env.REACT_APP_RELEASE || 'local',
    }),
  });

  if (!response.ok) {
    throw new Error(`Telemetry request failed with status ${response.status}`);
  }
}

export async function sendTelemetry(payload: Omit<TelemetryPayload, 'appSurface'>) {
  const fullPayload = { ...payload, appSurface: APP_SURFACE };

  try {
    await postTelemetry(fullPayload);
  } catch (error) {
    const queue = getQueue();
    queue.push(fullPayload);
    setQueue(queue);
    if (payload.level === 'error') {
      console.error('[AdMesh telemetry] failed to send', error);
    }
  }
}

export async function flushTelemetryQueue() {
  const queue = getQueue();
  if (!queue.length) return;

  const remaining: TelemetryPayload[] = [];
  for (const item of queue) {
    try {
      await postTelemetry(item);
    } catch {
      remaining.push(item);
    }
  }

  setQueue(remaining);
}

export function initTelemetry() {
  window.addEventListener('error', (event) => {
    void sendTelemetry({
      level: 'error',
      eventType: 'window_error',
      message: event.message,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason);
    void sendTelemetry({
      level: 'error',
      eventType: 'unhandled_rejection',
      message: reason,
    });
  });

  window.addEventListener('online', () => {
    void flushTelemetryQueue();
  });

  void flushTelemetryQueue();
}
